import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { Seal } from "@/components/brand/Seal";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export const Route = createFileRoute("/client/snapshot")({
  head: () => ({
    meta: [
      { title: "Family Snapshot — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Strategy Center" nav={CLIENT_NAV} tourKey="client_tour">
        <SnapshotPage />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface Member {
  id?: string;
  full_name: string;
  relationship: string;
  dob: string;
  is_dependent: boolean;
}

function SnapshotPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    dob: "",
    retirement_target_age: "",
    estimated_net_worth: "",
  });
  const [members, setMembers] = useState<Member[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!user) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function load() {
    if (!user) return;
    setLoading(true);
    const [profileRes, clientRes] = await Promise.all([
      supabase.from("profiles").select("full_name, phone").eq("id", user.id).maybeSingle(),
      supabase.from("clients").select("id, dob, retirement_target_age, estimated_net_worth").eq("user_id", user.id).maybeSingle(),
    ]);
    const cid = clientRes.data?.id ?? null;
    setClientId(cid);
    setForm({
      full_name: profileRes.data?.full_name ?? "",
      phone: profileRes.data?.phone ?? "",
      dob: (clientRes.data as any)?.dob ?? "",
      retirement_target_age: clientRes.data?.retirement_target_age != null ? String(clientRes.data.retirement_target_age) : "",
      estimated_net_worth: (clientRes.data as any)?.estimated_net_worth != null ? String((clientRes.data as any).estimated_net_worth) : "",
    });
    if (cid) {
      const { data } = await supabase
        .from("household_members")
        .select("id, full_name, relationship, dob, is_dependent")
        .eq("client_id", cid)
        .order("created_at", { ascending: true });
      setMembers(
        (data ?? []).map((m: any) => ({
          id: m.id,
          full_name: m.full_name ?? "",
          relationship: m.relationship ?? "",
          dob: m.dob ?? "",
          is_dependent: !!m.is_dependent,
        })),
      );
    } else {
      setMembers([]);
    }
    setRemovedIds([]);
    setLoading(false);
  }

  const completion = useMemo(() => {
    let done = 0;
    if (form.full_name) done++;
    if (form.dob) done++;
    if (form.retirement_target_age) done++;
    if (form.estimated_net_worth) done++;
    if (members.length > 0) done++;
    return { done, total: 5, pct: Math.round((done / 5) * 100) };
  }, [form, members]);

  function addMember() {
    setMembers((m) => [...m, { full_name: "", relationship: "", dob: "", is_dependent: true }]);
  }

  function updateMember(i: number, patch: Partial<Member>) {
    setMembers((arr) => arr.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }

  function removeMember(i: number) {
    const m = members[i];
    if (m.id) setRemovedIds((r) => [...r, m.id!]);
    setMembers((arr) => arr.filter((_, idx) => idx !== i));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      const profileUpdate = await supabase
        .from("profiles")
        .update({ full_name: form.full_name || null, phone: form.phone || null })
        .eq("id", user.id);
      if (profileUpdate.error) throw profileUpdate.error;

      const payload: any = {
        user_id: user.id,
        dob: form.dob || null,
        household_size: members.length + 1,
        retirement_target_age: form.retirement_target_age ? Number(form.retirement_target_age) : null,
        estimated_net_worth: form.estimated_net_worth ? Number(form.estimated_net_worth) : null,
      };
      const clientUpsert = await supabase
        .from("clients")
        .upsert(payload, { onConflict: "user_id" })
        .select("id")
        .single();
      if (clientUpsert.error) throw clientUpsert.error;
      const cid = clientUpsert.data.id;
      setClientId(cid);

      if (removedIds.length > 0) {
        const del = await supabase.from("household_members").delete().in("id", removedIds);
        if (del.error) throw del.error;
      }
      for (const m of members) {
        if (!m.full_name && !m.dob) continue;
        if (m.id) {
          const upd = await supabase
            .from("household_members")
            .update({
              full_name: m.full_name || "",
              relationship: m.relationship || "",
              dob: m.dob || null,
              is_dependent: m.is_dependent,
            } as any)
            .eq("id", m.id);
          if (upd.error) throw upd.error;
        } else {
          const ins = await supabase.from("household_members").insert({
            client_id: cid,
            full_name: m.full_name || "",
            relationship: m.relationship || "",
            dob: m.dob || null,
            is_dependent: m.is_dependent,
          } as any);
          if (ins.error) throw ins.error;
        }
      }
      toast.success("Family snapshot saved. Your Strategy Center is personalized.");
      await load();
    } catch (err: any) {
      toast.error(err?.message ?? "Could not save your snapshot.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <PortalPage
      eyebrow="Step 1 of 6"
      title="Your Family Snapshot"
      intro="The picture of your household — birthdays, dependents, retirement horizon, net worth. Everything that follows is tailored from these few details."
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <form onSubmit={handleSave} className="space-y-5">
          <Section title="About you">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name">
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Jane Smith" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" />
              </Field>
              <Field label="Date of birth">
                <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              </Field>
              <Field label="Target retirement age" hint="Used by the retirement horizon calculator.">
                <Input type="number" min={40} max={90} value={form.retirement_target_age} onChange={(e) => setForm({ ...form, retirement_target_age: e.target.value })} placeholder="65" />
              </Field>
              <Field label="Estimated net worth (USD)" hint="Surfaces estate-planning recommendations. Educational only.">
                <Input type="number" min={0} step={1000} value={form.estimated_net_worth} onChange={(e) => setForm({ ...form, estimated_net_worth: e.target.value })} placeholder="500000" />
              </Field>
            </div>
          </Section>

          <Section
            title="Your household"
            action={
              <button type="button" onClick={addMember} className="inline-flex items-center gap-1.5 text-[12px] font-sans uppercase tracking-[0.12em] text-bronze-600 hover:text-navy-800">
                <Plus className="h-4 w-4" /> Add person
              </button>
            }
          >
            {members.length === 0 ? (
              <p className="text-[14px] text-charcoal-500 italic">
                No dependents yet. Add a spouse, child, or anyone counting on you so we can size protection correctly.
              </p>
            ) : (
              <div className="space-y-3">
                {members.map((m, i) => (
                  <div key={m.id ?? `new-${i}`} className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_auto_auto] gap-2 items-end p-3 rounded-[4px] border border-bronze-600/20 bg-cream-50/60">
                    <Field label="Name">
                      <Input value={m.full_name} onChange={(e) => updateMember(i, { full_name: e.target.value })} placeholder="Full name" />
                    </Field>
                    <Field label="Relationship">
                      <Input value={m.relationship} onChange={(e) => updateMember(i, { relationship: e.target.value })} placeholder="Spouse, child…" />
                    </Field>
                    <Field label="Date of birth">
                      <Input type="date" value={m.dob} onChange={(e) => updateMember(i, { dob: e.target.value })} />
                    </Field>
                    <label className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.12em] text-charcoal-500 pb-2.5">
                      <input type="checkbox" checked={m.is_dependent} onChange={(e) => updateMember(i, { is_dependent: e.target.checked })} />
                      Dependent
                    </label>
                    <button type="button" onClick={() => removeMember(i)} className="text-charcoal-500 hover:text-danger p-2 pb-2.5" aria-label="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <div className="flex items-center justify-between gap-3 pt-2">
            <p className="text-[12px] text-charcoal-500">
              Educational only — not legal, tax, or investment advice.
            </p>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => navigate({ to: "/client/calculators" })} className="text-[12px] uppercase tracking-[0.12em] font-sans text-charcoal-500 hover:text-navy-800">
                Back to Strategy Center
              </button>
              <PrimaryButton type="submit" disabled={saving || loading}>
                {saving ? "Saving…" : "Save snapshot"}
              </PrimaryButton>
            </div>
          </div>
        </form>

        <aside className="space-y-4">
          <div className="bg-navy-800 text-cream-50 p-6 rounded-[4px] shadow-soft">
            <Eyebrow>Snapshot</Eyebrow>
            <div className="mt-4 flex items-center justify-center">
              <Seal title={`${completion.pct}%`} subtitle="Complete" size="md" />
            </div>
            <p className="mt-4 text-[13px] leading-[1.6] text-cream-50/80 text-center">
              {completion.done === completion.total
                ? "Your snapshot is complete. Every recommendation is now tailored to your family."
                : `${completion.total - completion.done} detail${completion.total - completion.done === 1 ? "" : "s"} left to personalize your plan.`}
            </p>
          </div>
          <div className="bg-white border border-bronze-600/20 p-5 rounded-[4px]">
            <Eyebrow>Why we ask</Eyebrow>
            <ul className="mt-3 space-y-2 text-[13px] text-charcoal-900 leading-[1.6]">
              <li>• <span className="text-navy-800 font-medium">DOB & dependents</span> size your DIME protection need.</li>
              <li>• <span className="text-navy-800 font-medium">Retirement age</span> sets your time horizon.</li>
              <li>• <span className="text-navy-800 font-medium">Net worth</span> triggers estate-planning guidance.</li>
            </ul>
          </div>
        </aside>
      </div>
    </PortalPage>
  );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-display text-navy-800 text-[22px]">{title}</h3>
        {action}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-sans text-[12px] uppercase tracking-[0.16em] text-charcoal-500">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-charcoal-500">{hint}</p>}
    </div>
  );
}